import { request } from 'umi';
import {
  AssetCatalog,
  AssetLocation,
  SingleSource,
  AssetFields,
} from '@/types/sources';
import { AssetFieldsOrder } from '@/utils/sourceFields';
import { CreateAssets } from '@/types/formType';
import { AssetStatusFilter } from '@/types';

export interface PersonnelSource {
  ITTag: string;
  assetTypeID: string;
  category: string;
  code: string;
  company: string;
  extensionsJSON: string;
  financeTag: string;
  id: string;
  location: string;
  model: string;
  name: string;
  remark: string;
  serialNumber: string;
  status: string;
  user: string;
  warrantyExpiredAt: string;
  warrantyWarning: string;
}

export const getDefaultRequest = (): Promise<{
  defaultAssetTypeConf: AssetFields[];
}> => {
  return request('/assets/defaultConf');
};

export const createSource = (
  data: Omit<SingleSource, 'id'>,
): Promise<{ assetTypeID: string }> => {
  return request('/assetType', {
    method: 'post',
    data,
  });
};

export const createAssets = ({
  assetTypeID,
  assets,
}: CreateAssets): Promise<{ assetTypeID: string }> => {
  return request(`/assertTypes/${assetTypeID}/assets`, {
    method: 'post',
    data: {
      assets,
    },
  });
};

export const getAllSourceInfo = (): Promise<{
  assetTypes: SingleSource[];
}> => {
  return request('/assetTypes');
};

export const getAllAssetTypes = (): Promise<{
  assetTypes: SingleSource[];
}> => {
  return request('/assetTypes/all');
};

export const getSourceInfoBySourceId = (
  id: string,
  search: string[],
): Promise<{ assets: { [key in string]: string | number }[] }> => {
  let searchUrl = '';
  if (search.length) {
    searchUrl = `?${search.map((s) => `searchList=${s}`).join('&')}`;
  }
  return request(`/assertTypes/${id}/assets${searchUrl}`);
};

export const deleteSourceById = (id: string) => {
  return request(`/assetType/${id}`, {
    method: 'delete',
  });
};

export const DeleteAssetsByIds = (assetTypeID: string, ids: string[]) => {
  const queryStr = ids.reduce((a, b) => `${a ? `${a}&` : a}assetIDs=${b}`, '');
  return request(`/assertTypes/${assetTypeID}/assets?${queryStr}`, {
    method: 'delete',
  });
};

export const updateSingleSourceTypeInfo = (
  id: string,
  data: Omit<SingleSource, 'id'>,
) => {
  return request(`/assetTypes/${id}`, {
    method: 'put',
    data,
  });
};

export const updateAssetsInfo = (
  id: string,
  assetTypeId: string,
  data: any,
) => {
  return request(`/assertTypes/${assetTypeId}/assets/${id}`, {
    method: 'put',
    data,
  });
};

export const requestAssetAllocation = (
  id: string,
): Promise<{
  data: AssetLocation[];
}> => {
  return request(`/assets/countByLocation?assetTypeID=${id}`);
};

export const requestAssetCategory = (
  id: string,
): Promise<{ data: AssetCatalog[] }> => {
  return request(`/assets/countByCategory?assetTypeID=${id}`);
};

export const requestAssetStatus = (
  data?: AssetStatusFilter,
): Promise<{
  data: { status: string; num: number }[];
}> => {
  return request(`/assets/countByStatus`, {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    data: data ?? {},
  });
};

export const requestAssetTypeCount = (): Promise<{
  data: { assetTypeID: string; assetTypeName: string; num: string }[];
}> => {
  return request('/assets/countByType');
};

export const requestFieldsOrder = (
  id: string,
): Promise<{
  sorts: AssetFieldsOrder[];
}> => {
  return request(`users/assertTypes/${id}/sortConf`);
};

export const requestUpdateFieldsOrder = (
  id: string,
  data: AssetFieldsOrder[],
) => {
  return request(`users/assertTypes/${id}/sortConf`, {
    data: {
      sorts: data,
    },
    method: 'post',
  });
};

// 获取用户名下的所有资产
export const requestPersonnelSources = (username: string) =>
  request<{ assets: PersonnelSource[] }>(`listAllAssets/${username}`);

export const requestUserInventorySnapshot = (inventoryID: string) =>
  request<{ assets: PersonnelSource[] }>(`inventories/${inventoryID}/assets`);
